import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

/**
 * Formats a segment period into a human-readable label
 * @param {string} startLocal - Period start in local format (e.g., "2025-12-01")
 * @param {string} endLocal - Period end in local format (e.g., "2025-12-31")
 * @returns {string} Formatted period label
 */
export const formatSegmentPeriod = (startLocal, endLocal) => {
  const start = dayjs(startLocal);
  const end = dayjs(endLocal);

  // If same month
  if (start.month() === end.month() && start.year() === end.year()) {
    if (start.date() === 1 && end.date() === start.endOf('month').date()) {
      return start.format('MMMM YYYY');
    }
    return `${start.format('MMM D')} - ${end.format('D, YYYY')}`;
  }

  // If different months in same year
  if (start.year() === end.year()) {
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  }

  // If spans years
  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
};

/**
 * Analyzes segments to identify which are unfilled
 * @param {Array} segments - Array of segment objects
 * @returns {Array} Array of unfilled segment info
 */
export const getUnfilledSegments = (segments) => {
  if (!segments || !Array.isArray(segments)) return [];

  return segments
    .filter((seg) => !seg.isFilled)
    .map((seg) => ({
      periodStart: seg.periodStartLocal,
      periodEnd: seg.periodEndLocal,
      availableSlots: seg.availableSlots,
      // Format human-readable period label
      label: formatSegmentPeriod(seg.periodStartLocal, seg.periodEndLocal),
    }));
};

/**
 * Condenses a list of segments into a short human-readable string
 * (e.g., "January, February" or "Week 4, Week 5")
 * @param {Array} segments - Array of unfilled segments
 * @returns {string} Condensed string
 */
export const getCondensedPeriodList = (segments) => {
  if (!segments || !Array.isArray(segments) || segments.length === 0) return '';

  const labels = segments.map((seg) => {
    const start = dayjs(seg.periodStartLocal);
    const end = dayjs(seg.periodEndLocal);
    const diffDays = end.diff(start, 'day') + 1;

    // Detect Month: Starts on 1st and covers a whole month
    if (start.date() === 1 && end.date() === start.endOf('month').date()) {
      return start.format('MMMM');
    }

    // Detect Week: 7 days (or close to it)
    // snapped to start of week if it's 7 days
    if (diffDays >= 6 && diffDays <= 8) {
      return `Week ${start.isoWeek()}/${start.format('YY')}`;
    }

    // Fallback: use the standard period formatter
    return formatSegmentPeriod(seg.periodStartLocal, seg.periodEndLocal);
  });

  // Unique labels and join
  const uniqueLabels = [...new Set(labels)];

  if (uniqueLabels.length <= 3) {
    return uniqueLabels.join(', ');
  }

  // If too many, show first two and a "..." or count
  return `${uniqueLabels.slice(0, 2).join(', ')} ...`;
};

/**
 * Determines the appropriate message for a planning gap badge
 * @param {Object} gap - The planning gap object
 * @param {Function} t - Translation function (optional)
 * @returns {string} Badge message to display
 */
export const formatGapProgress = (gap, t = null) => {
  const missingCount = gap.missingCount || 0;
  const useSeats = gap.reason === 'insufficient_timeslots' || gap.reason === 'no_timeslots_available';
  const translationKey = useSeats
    ? 'lecturer.eventCalendar.gaps.missingSeatsCount'
    : 'lecturer.eventCalendar.gaps.missingSlotsCount';

  // Format with proper pluralization
  if (t) {
    return t(translationKey, {
      count: missingCount,
    });
  }

  // Fallback without translation
  if (useSeats) {
    return missingCount === 1 ? '1 seat' : `${missingCount} seats`;
  }
  return missingCount === 1 ? '1 slot' : `${missingCount} slots`;
};

/**
 * Formats the reason text for display
 * @param {string} reason - Snake case reason code
 * @returns {string} Human-readable reason
 */
export const formatReasonText = (reason) => {
  if (!reason) return '';

  return reason
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Generates a detailed description of what's missing
 * @param {Object} gap - The planning gap object
 * @param {Function} t - Translation function
 * @returns {string} Detailed description
 */
export const getGapDetail = (gap, t) => {
  const { reason, segments } = gap;

  // 1. Prioritize detailed segment breakdown if available
  if (segments && Array.isArray(segments) && segments.length > 0) {
    const unfilledSegments = segments.filter((s) => !s.isFilled);

    // If we have unfilled segments, show them
    if (unfilledSegments.length > 0) {
      const condensed = getCondensedPeriodList(unfilledSegments);
      if (condensed) {
        return (
          t('lecturer.eventCalendar.gaps.missingIn', {
            periods: condensed,
          }) || `Missing in: ${condensed}`
        );
      }
    }
  }

  // 2. Specific logic for critical first segment
  if (reason === 'critical_first_segment_missing' || reason === 'first_segment_missing') {
    return t('lecturer.eventCalendar.gaps.firstSegmentMissing') || 'First time period must be scheduled';
  }

  // 3. Translation-based fallbacks for known reason codes
  const knownReasons = {
    insufficient_coverage: 'insufficientCoverage',
    critical_insufficient_coverage: 'insufficientCoverage',
    minor_insufficient_coverage: 'insufficientCoverage',
    segment_shortage: 'insufficientCoverage',
    critical_segment_shortage: 'insufficientCoverage',
    minor_segment_shortage: 'insufficientCoverage',
  };

  if (reason && knownReasons[reason]) {
    const translationKey = `lecturer.eventCalendar.gaps.${knownReasons[reason]}`;
    return t(translationKey) || 'Not enough timeslots available';
  }

  // 4. Final fallback: convert the raw reason string to a readable format
  if (reason && reason !== 'ok') {
    return formatReasonText(reason);
  }

  return '';
};
